class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;
    this.requestId = 0;
    this.abortController = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form || !this.input || !this.predictiveSearchResults) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    const scheduleSearch = debounce(this.onChange.bind(this), 300);
    const onInput = () => {
      // Invalidate immediately, before the debounce: an older response can
      // otherwise render while the customer is still typing or clearing.
      this.close(!this.getQuery().length);
      if (this.getQuery().length) scheduleSearch();
    };
    this.input.addEventListener("input", onInput);

    if (this.select) {
      this.select.addEventListener("input", onInput);
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  getQueryKey(searchTerm = this.getQuery()) {
    return JSON.stringify([
      searchTerm,
      this.getSearchFilterType(),
      this.getSearchFilterTypeQuery(),
      this.dataset.limit,
      this.dataset.limitScope,
    ]);
  }

  cancelPendingSearch() {
    this.requestId += 1;
    this.abortController?.abort();
    this.abortController = null;
    this.removeAttribute("loading");
  }

  disconnectedCallback() {
    this.cancelPendingSearch();
    clearTimeout(this.liveRegionTimer);
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    if (!this.contains(document.activeElement)) return;

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      (this.isOpen &&
        this.querySelector(
          '[aria-selected="true"] a, button[aria-selected="true"], [aria-selected="true"] button',
        ))
    ) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault();
        this.switchOption("up");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
      case "Escape":
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (this.isOpen && (event.code === "ArrowUp" || event.code === "ArrowDown")) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    if (!this.isOpen) return;
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"], [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  async getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey = this.getQueryKey(searchTerm);

    this.cancelPendingSearch();
    if (
      !searchTerm.length ||
      queryKey !== this.getQueryKey() ||
      !this.contains(document.activeElement)
    ) return;

    const requestId = this.requestId;
    const isCurrent = () =>
      requestId === this.requestId &&
      queryKey === this.getQueryKey() &&
      this.contains(document.activeElement);

    this.setLiveRegionLoadingState();

    if (Object.hasOwn(this.cachedResults, queryKey)) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);
    const controller = new AbortController();
    this.abortController = controller;

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`,
        { signal: controller.signal },
      );
      if (!isCurrent()) return;
      if (!response.ok) throw new Error(`Predictive search: ${response.status}`);

      const text = await response.text();
      if (!isCurrent()) return;
      const section = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-predictive-search");
      if (!section) throw new Error("Predictive search section is missing");

      this.cachedResults[queryKey] = section.innerHTML;
      this.renderSearchResults(section.innerHTML);
    } catch (error) {
      // A superseded request must not close newer results. Ordinary failures
      // leave the native search form available instead of rejecting globally.
      if (isCurrent()) this.close();
    } finally {
      if (requestId === this.requestId) {
        this.abortController = null;
        this.removeAttribute("loading");
      }
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    clearTimeout(this.liveRegionTimer);
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    this.liveRegionTimer = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")?.textContent || "",
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsOpen);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingSearch();
    clearTimeout(this.liveRegionTimer);
    if (this.statusElement) {
      this.statusElement.textContent = "";
      this.statusElement.setAttribute("aria-hidden", "true");
    }
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsClose);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);

